
import { useState, useCallback, useEffect } from 'react';
import { api } from '@/lib/api';

export interface UserStats {
  total: number;
  byRole: {
    admin: number;
    producer: number;
    moderator: number;
    congregant: number;
  };
  active: number;
  inactive: number;
}

export interface PrayerStats {
  total: number;
  thisWeek: number;
  today: number;
  byStatus: {
    pending: number;
    approved: number;
    displayed: number;
    archived: number;
    dismissed: number;
    answered: number;
    queued?: number;
  };
  byCategory: {
    [category: string]: number;
  };
  byType: {
    prayer_request: number;
    praise_report: number;
  };
}

export interface SystemStats {
  activeConnections: number;
  uptime: number;
  lastPrayerSubmission: string | null;
}

export interface DashboardStats {
  users: UserStats;
  prayers: PrayerStats;
  system: SystemStats;
}

const DEFAULT_STATS: DashboardStats = {
  users: {
    total: 0,
    byRole: { admin: 0, producer: 0, moderator: 0, congregant: 0 },
    active: 0,
    inactive: 0,
  },
  prayers: {
    total: 0,
    thisWeek: 0,
    today: 0,
    byStatus: {
      pending: 0,
      approved: 0,
      displayed: 0,
      archived: 0,
      dismissed: 0,
      answered: 0,
      queued: 0,
    },
    byCategory: {},
    byType: { prayer_request: 0, praise_report: 0 },
  },
  system: {
    activeConnections: 0,
    uptime: 0,
    lastPrayerSubmission: null,
  },
};

interface UseStatsReturn {
  stats: DashboardStats;
  isLoading: boolean;
  error: string | null;
  fetchStats: () => Promise<void>;
  refreshStats: () => Promise<void>;
}

export function useStats(): UseStatsReturn {
  const [stats, setStats] = useState<DashboardStats>(DEFAULT_STATS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get<DashboardStats>('/admin/stats');
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        setStats(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshStats = useCallback(async () => {
    // Don't set isLoading to avoid flicker on refresh
    setError(null);
    try {
      const response = await api.get<DashboardStats>('/admin/stats');
      if (response.error) {
        setError(response.error);
      } else if (response.data) {
        setStats(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh stats');
    }
  }, []);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Auto-refresh stats every 30 seconds
  useEffect(() => {
    const interval = setInterval(refreshStats, 30000);
    return () => clearInterval(interval);
  }, [refreshStats]);

  return {
    stats,
    isLoading,
    error,
    fetchStats,
    refreshStats,
  };
}
